import { HTMLProps, useEffect, useState } from 'react'
import GetUrls from 'ts/enums/Url'
import { User, UserType } from 'ts/types'
import useModalStore from 'store/modal'
import useModalForm from 'hooks/useModalForm'
import { CheckBox, Input, Select, CheckBoxToggle, Button } from 'components/ui'
import ButtonBasic from 'components/ui/ButtonBasic'
import ButtonOutline from 'components/ui/ButtonOutline'
import useFetchSafe from 'hooks/useFetchSafe'
import PasswordChangeModal from './PasswordChangeModal'
import { http } from 'utils'
import toast from 'react-hot-toast'
import { useTrader } from 'context/TraderTypeContext'
import { useForm } from 'react-hook-form'
import ModalCard from 'components/ModalCard'
import useAuthStore from 'store/auth'
import useDataTableStore from 'store/dataTable'

/** API encoding: positive = failed attempt count; 0 = none; -1 = must change password (not a count); -2.. = |value| = attempts, ≤-3 = locked */
function getInvalidLoginsDisplay(raw: number): {
  title: string
  detail?: string
  tone: 'success' | 'warning' | 'danger'
} {
  if (raw > 0) {
    return {
      title: String(raw),
      detail: raw === 1 ? 'Invalid login attempt' : 'Invalid login attempts',
      tone: raw >= 3 ? 'danger' : 'warning',
    }
  }
  if (raw === 0) {
    return { title: '0', tone: 'success' }
  }
  if (raw === -1) {
    return {
      title: 'Password change required',
      tone: 'warning',
    }
  }
  const failures = Math.abs(raw)
  const locked = raw <= -3
  return {
    title: String(failures),
    detail: locked ? 'failed attempts · account locked' : 'failed login attempts',
    tone: 'danger',
  }
}

const toneClass: Record<'success' | 'warning' | 'danger', string> = {
  success: 'text-green-600',
  warning: 'text-red-500',
  danger: '!text-red-600',
}

const UserAddPage = () => {
  const Url = GetUrls();
  const { traderId } = useTrader();
  const user = useAuthStore((state) => state.user);
  const params = useModalStore((state) => state.params) as User
  const { ModalForm, form: modalForm } = useModalForm()
  const { setValue: setModalValue, register: registerModal } = modalForm
  const reload = useDataTableStore((state) => state.reload)
  const onRequestClose = useModalStore((state) => state.onRequestClose)
  
  // Custom form for new users
  const customForm = useForm();
  const { setValue, register, handleSubmit } = customForm
  
  const { data } = useFetchSafe<UserType>(Url.getUserType)
  const [userTypes, setUserTypes] = useState<HTMLProps<HTMLOptionElement>[]>([])
  const [invalidLogins, setInvalidLogins] = useState<number>(0)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  
  useEffect(() => {
    if(params) {
      console.log("params",params);
      setModalValue('programUserId', params.programUserId);
    } else {
      if (traderId) {
        setValue('traderId', Number(traderId));
      }
      if (user?.programId) {
        setValue('programId', Number(user.programId));
      }
    }
  }, [params, traderId, user, setValue, setModalValue])
  
  useEffect(() => {
    setUserTypes(
      data.map((userType) => ({
        value: userType.userTypeId,
        children: userType.name
      }))
    )
  }, [data])

  useEffect(() => {
    if (params && params.invalidLogins != null) {
      setInvalidLogins(params.invalidLogins)
    }
  }, [params])

  const handleOpenPasswordModal = () => {
    setIsPasswordModalOpen(true)
  }

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false)
  }

  const handlePasswordChangeSuccess = (wasUnlocked: boolean) => {
    if (wasUnlocked) {
      setInvalidLogins(0)
    }
  }

  const handleNewUserSubmit = async (formData: any) => {
    setIsLoading(true);
    try {
      const userData = {
        programId: formData.programId,
        flActive: formData.flActive,
        name: formData.name,
        emailAddress: formData.emailAddress,
        login: formData.login,
        userTypeId: formData.userTypeId,
        traderId: [formData.traderId]
      };

      const userResponse = await http.request({
        url: Url.postUser,
        method: 'POST',
        data: userData
      });

      const createdUser = userResponse.data as any;
      const programUserId = createdUser.programUserId;

      if (!programUserId) {
        throw new Error('Failed to get programUserId from user creation response');
      }

      const passwordData = {
        programUserId: programUserId,
        adminPassword: formData.adminPassword,
        newPassword: formData.newPassword,
        requireChange: formData.requirePasswordChange,
        unlock: true
      };

      await http.request({
        url: Url.postChangeUserPassword,
        method: 'POST',
        data: passwordData
      });

      toast.success('User created successfully');
      onRequestClose();
      setTimeout(() => reload(), 0);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create user');
    } finally {
      setIsLoading(false);
    }
  };

  if (params) {
    const invalidLoginStatus = getInvalidLoginsDisplay(invalidLogins)
    return (
      <>
        <ModalForm 
          config={{ url: Url.postUser, method: 'PUT' }}
          title="Edit User"
          description='Edit an existing User'
        >
          <div className='flex justify-between'>
            <div className="w-1/2">
              <Input defaultValue={params.programUserId} label="ID" disabled />
              <CheckBox
                defaultChecked={params?.flActive || false}
                label="Active"
                {...registerModal('flActive')}
              />
              <Input
                id="name"
                defaultValue={params?.name || ''}
                label="Name"
                {...registerModal('name', { required: true })}
              />
              <Input
                defaultValue={params?.emailAddress || ''}
                label="E-mail Address"
                type="email"
                {...registerModal('emailAddress', { required: true })}
              />
              <Input
                defaultValue={params?.login || ''}
                label="Username"
                {...registerModal('login', { required: true })}
              />
              
              {userTypes.length > 0 ?
                <Select
                  defaultValue={params?.userTypeId?.toString() || ''}
                  label="User Type"
                  placeholder='Select a User Type...'
                  options={userTypes}
                  {...registerModal('userTypeId', { required: true, valueAsNumber: true })}
                />
                :
                <Select
                    disabled
                    className='opacity-70'
                    label="User Type"
                    defaultValue={"placeholder"}
                    options={[{ value:"", children:"" }]}
                  />
              }
            </div>

            {/* Right Column */}
            <div className='ml-8 w-1/2'>
              <div className='flex flex-col'>
                <p className='font-semibold text-sm'>Invalid logins</p>
                {(() => {
                  const { title, detail, tone } = getInvalidLoginsDisplay(invalidLogins)
                  return (
                    <>
                      <p className={`text-xl mt-0.5 font-bold ${toneClass[tone]}`}>{title}</p>
                      {detail ? (
                        <p className="text-sm text-gray-600 mt-1">{detail}</p>
                      ) : null}
                    </>
                  )
                })()}
              </div>
              
              <div className='flex flex-col mt-4'>
                <p className='font-semibold text-sm'>Password and Account Recovery</p>

                <ButtonBasic 
                  type="button"
                  className='mt-1 w-52' 
                  isLoading={false}
                  onClick={handleOpenPasswordModal}
                >
                  Open
                </ButtonBasic>
              </div>
            </div>
          </div>
        </ModalForm>

        {/* Password Change Modal */}
        <PasswordChangeModal
          user={params}
          isOpen={isPasswordModalOpen}
          onClose={handleClosePasswordModal}
          onPasswordChangeSuccess={handlePasswordChangeSuccess}
        />
      </>
    );
  }

  return (
    <>
      <ModalCard title="Create User" description="Create a new User">
        <form onSubmit={handleSubmit(handleNewUserSubmit)} className="flex flex-col w-full">
          <div className='flex justify-between'>
            {/* Left Column - User Details */}
            <div className="w-1/2">
              <CheckBox
                defaultChecked={true}
                label="Active"
                {...register('flActive')}
              />
              <Input
                id="name"
                label="Name"
                {...register('name', { required: true })}
              />
              <Input
                label="E-mail Address"
                type="email"
                {...register('emailAddress', { required: true })}
              />
              <Input
                label="Username"
                {...register('login', { required: true })}
              />
              
              {userTypes.length > 0 ?
                <Select
                  label="User Type"
                  placeholder='Select a User Type...'
                  options={userTypes}
                  {...register('userTypeId', { required: true, valueAsNumber: true })}
                />
                :
                <Select
                    disabled
                    className='opacity-70'
                    label="User Type"
                    defaultValue={"placeholder"}
                    options={[{ value:"", children:"" }]}
                  />
              }
            </div>

            {/* Right Column - Password Fields */}
            <div className='ml-8 w-1/2'>
              <div className="space-y-4">
                <CheckBoxToggle 
                  defaultChecked={false} 
                  label="Require Password Change"
                  onToggle={(isChecked: boolean) => setValue('requirePasswordChange', isChecked)}
                />
                <Input
                  label="New Password"
                  type="password"
                  {...register('newPassword', { required: true })}
                />
                <hr/>
                <Input
                  label="Admin Password"
                  type="password"
                  {...register('adminPassword', { required: true })}
                />
              </div>
            </div>
          </div>
          
          {/* Submit buttons with consistent styling */}
          <div className='flex'>
            <Button
              isLoading={isLoading}
              className="w-40 mt-4 z-20 relative"
            >
              Create User
            </Button>
            <ButtonOutline
              onClick={(e) => { e.preventDefault(); onRequestClose(); }}
            >
              Cancel
            </ButtonOutline>
          </div>
        </form>
      </ModalCard>
    </>
  )
}

export default UserAddPage